/**
 * @file FlatRateForm.tsx
 *
 * Inline controlled mini-form for adding or editing a flat-rate shipping method
 * within the zone form (no react-hook-form). Fields: title, tax status (a native
 * `<select>` — the Gravity `Select` web component isn't drivable under jsdom),
 * base cost, and an optional additional fee (a toggle enabling its cost field). Validates title
 * (required) and the cost/fee (finite, non-negative) on save, then reports the
 * clean {@link FlatRateData} to its parent. Mirrors WSB-v2's flat-rate sub-form.
 */

import { useId, useState } from 'react';
import { Button, Divider, GeneralField, Icon, Toggle } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { emptyFlatRateData, isInvalidRequiredAmount } from './custom-shipping-form-utils';
import type { FlatRateData } from './custom-shipping-form-types';

interface FlatRateFormProps {
  prev?: FlatRateData;
  currencySymbol: string;
  /** Renders a divider + heading above the form (when added to a non-empty list). */
  showDivider?: boolean;
  onSubmit: (data: FlatRateData) => void;
  onCancel: () => void;
}

interface FlatRateErrors {
  title?: string;
  cost?: string;
  additionalFeeCost?: string;
}

/**
 * Renders the flat-rate method mini-form.
 *
 * @param props - The seed data (edit), currency symbol, divider flag, and the
 *   submit/cancel handlers.
 *
 * @returns The mini-form element.
 */
export function FlatRateForm({
  prev,
  currencySymbol,
  showDivider = false,
  onSubmit,
  onCancel,
}: FlatRateFormProps) {
  const intl = useIntl();
  const fieldId = useId();
  const [data, setData] = useState<FlatRateData>(prev ?? emptyFlatRateData());
  const [errors, setErrors] = useState<FlatRateErrors>({});

  const patch = (update: Partial<FlatRateData>): void => {
    setData((prevData) => ({ ...prevData, ...update }));
  };

  const handleSave = (): void => {
    const next: FlatRateErrors = {};
    if (data.title.trim() === '') {
      next.title = intl.formatMessage(adminMessages.shippingMethodTitleRequired);
    }
    if (isInvalidRequiredAmount(data.cost)) {
      next.cost = intl.formatMessage(adminMessages.shippingAmountInvalid);
    }
    if (data.additionalFeeEnabled && isInvalidRequiredAmount(data.additionalFeeCost)) {
      next.additionalFeeCost = intl.formatMessage(adminMessages.shippingAmountInvalid);
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      return;
    }
    onSubmit({
      ...data,
      title: data.title.trim(),
      cost: data.cost.trim(),
      additionalFeeCost: data.additionalFeeEnabled ? data.additionalFeeCost.trim() : '0',
    });
  };

  return (
    <div className="gv-flex gv-flex-col gv-gap-md">
      {showDivider && (
        <div className="gv-flex gv-flex-col gv-gap-sm">
          <Divider />
          <div className="gv-text-sm gv-text-bold">
            <FormattedMessage
              {...(prev ? adminMessages.shippingMethodEditHeading : adminMessages.shippingMethodAddNewHeading)}
            />
          </div>
        </div>
      )}

      <div className="gv-form-option gv-w-full">
        <label className="gv-label" htmlFor={`${fieldId}-title`}>
          <FormattedMessage {...adminMessages.shippingMethodTitleLabel} />
        </label>
        <GeneralField
          id={`${fieldId}-title`}
          value={data.title}
          error={errors.title}
          data-testid="flat-rate-form-title"
          aria-label={intl.formatMessage(adminMessages.shippingMethodTitleLabel)}
          onChange={(event) => patch({ title: event.target.value })}
        />
      </div>

      <div className="gv-form-option gv-w-full">
        <label className="gv-label" htmlFor={`${fieldId}-tax`}>
          <FormattedMessage {...adminMessages.shippingTaxStatusLabel} />
        </label>
        <div className="gv-input gv-input-select gv-w-full">
          <select
            id={`${fieldId}-tax`}
            value={data.taxStatus}
            data-testid="flat-rate-form-tax-status"
            aria-label={intl.formatMessage(adminMessages.shippingTaxStatusLabel)}
            onChange={(event) => patch({ taxStatus: event.target.value })}
          >
            <option value="taxable">
              {intl.formatMessage(adminMessages.shippingTaxStatusTaxable)}
            </option>
            <option value="none">{intl.formatMessage(adminMessages.shippingTaxStatusNone)}</option>
          </select>
          <Icon name="expand_more" size="small" />
        </div>
      </div>

      <div className="gv-form-option gv-w-full">
        <label className="gv-label" htmlFor={`${fieldId}-cost`}>
          <FormattedMessage {...adminMessages.shippingCostLabel} />
        </label>
        <GeneralField
          id={`${fieldId}-cost`}
          value={data.cost}
          inputMode="decimal"
          addon={currencySymbol}
          error={errors.cost}
          data-testid="flat-rate-form-cost"
          aria-label={intl.formatMessage(adminMessages.shippingCostLabel)}
          onChange={(event) => patch({ cost: event.target.value })}
        />
        <p className="gv-caption-lg">
          <FormattedMessage {...adminMessages.shippingCostHelp} />
        </p>
      </div>

      <div className="gv-flex gv-flex-col gv-gap-sm">
        <span className="gv-text-sm gv-text-bold">
          <FormattedMessage {...adminMessages.shippingAdditionalFeeHeading} />
        </span>
        <label className="gv-flex gv-items-center gv-gap-sm">
          <Toggle
            condensed
            checked={data.additionalFeeEnabled}
            onChange={(checked) => patch({ additionalFeeEnabled: checked })}
          />
          <span className="gv-text-sm">
            <FormattedMessage {...adminMessages.shippingAdditionalFeeLabel} />
          </span>
        </label>
      </div>

      <div className="gv-form-option gv-w-full">
        <label className="gv-label" htmlFor={`${fieldId}-fee`}>
          <FormattedMessage {...adminMessages.shippingAdditionalFeeCostLabel} />
        </label>
        <GeneralField
          id={`${fieldId}-fee`}
          value={data.additionalFeeCost}
          inputMode="decimal"
          addon={currencySymbol}
          disabled={!data.additionalFeeEnabled}
          error={errors.additionalFeeCost}
          aria-label={intl.formatMessage(adminMessages.shippingAdditionalFeeCostLabel)}
          onChange={(event) => patch({ additionalFeeCost: event.target.value })}
        />
      </div>

      <div className="gv-flex gv-gap-sm gv-flex-col-reverse gv-tab-flex-row">
        <Button
          variant="cancel"
          condensed
          data-testid="flat-rate-form-cancel"
          onClick={onCancel}
        >
          <FormattedMessage {...adminMessages.shippingMethodCancel} />
        </Button>
        <Button
          variant="primary"
          condensed
          data-testid="flat-rate-form-done"
          onClick={handleSave}
        >
          <FormattedMessage {...adminMessages.shippingMethodSave} />
        </Button>
      </div>
    </div>
  );
}
